import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Search, Download, Filter, ChevronLeft, ChevronRight, Globe, DollarSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getFlagEmoji = (iso) => {
  if (!iso || !/^[A-Za-z]{2}$/.test(iso)) return "🌍";

  const specialCases = {
    AB: "🇬🇪",
    AC: "🇸🇭",
    AN: "🇳🇱",
    XK: "🇽🇰",
  };

  if (specialCases[iso]) return specialCases[iso];

  try {
    const codePoints = iso
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  } catch {
    return "🌍";
  }
};

const currencies = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "Pound" },
  { code: "INR", symbol: "₹", name: "Rupee" },
];

const formatPlanRate = (value, symbol) => {
  if (value == null) {
    return "—";
  }

  return `${symbol}${Number(value).toFixed(4)}`;
};

const RatesPage = () => {
  const [rates, setRates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;

  const fetchCountries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/countries`);
      setCountries(res.data.countries || []);
    } catch (e) {
      console.error("Failed to fetch countries", e);
    }
  }, []);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        currency: selectedCurrency,
      };

      if (search) params.search = search;
      if (selectedCountry && selectedCountry !== "all") params.country = selectedCountry;

      const res = await axios.get(`${API}/rates/compare`, { params });
      setRates(res.data.data || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error("Failed to fetch rates", e);
    } finally {
      setLoading(false);
    }
  }, [page, search, selectedCountry, selectedCurrency]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setPage(1);
      fetchRates();
    }, 300);

    return () => clearTimeout(debounce);
  }, [search, selectedCountry, selectedCurrency]);

  useEffect(() => {
    fetchRates();
  }, [page, fetchRates]);

  const handleDownload = () => {
    window.open(`${API}/rates/download-all`, "_blank");
  };

  const getCurrencySymbol = () => {
    const curr = currencies.find((currency) => currency.code === selectedCurrency);
    return curr ? curr.symbol : "$";
  };

  const currencySymbol = getCurrencySymbol();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="rates-page">
      <section className="bg-slate-900 text-white py-20 noise-bg" data-testid="rates-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-4">Call Rates</h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Compare Silver, Gold, and Platinum rates in one place for 235+ destinations.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="bg-white border-b py-4" data-testid="currency-selector">
        <div className="container-custom">
          <div className="flex justify-end items-center gap-2">
            <DollarSign className="w-5 h-5 text-slate-500" />
            <span className="text-sm text-slate-500 mr-2">Currency:</span>
            <div className="flex gap-1 flex-wrap justify-end">
              {currencies.map((curr) => (
                <Button
                  key={curr.code}
                  variant={selectedCurrency === curr.code ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCurrency(curr.code)}
                  className={`px-3 ${selectedCurrency === curr.code ? "bg-brand-500 hover:bg-brand-600" : ""}`}
                  data-testid={`currency-${curr.code.toLowerCase()}`}
                >
                  {curr.symbol} {curr.code}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="sticky top-20 bg-white shadow-md z-40 py-4" data-testid="rates-filters">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by destination, country or prefix..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 py-6 w-full rounded-xl border-slate-200 focus:border-brand-400"
                data-testid="rates-search-input"
              />
            </div>

            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger className="w-full lg:w-[260px] py-6 rounded-xl" data-testid="rates-country-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">🌍 All Countries</SelectItem>
                {countries.map((country) => (
                  <SelectItem key={country.iso || country.name} value={country.iso || country.name}>
                    {getFlagEmoji(country.iso)} {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              onClick={handleDownload}
              className="bg-brand-500 hover:bg-brand-600 text-white rounded-xl px-6 py-6 whitespace-nowrap"
              data-testid="rates-download-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Rates ZIP
            </Button>
          </div>

          <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
            <span data-testid="rates-count">Showing {rates.length} of {total.toLocaleString()} routes</span>
            <span>Page {page} of {totalPages}</span>
          </div>
        </div>
      </section>

      <section className="py-8" data-testid="rates-table-section">
        <div className="container-custom">
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="animate-pulse">
                {[...Array(10)].map((_, index) => (
                  <div key={index} className="flex gap-4 p-4 border-b border-slate-100">
                    <div className="h-5 bg-slate-200 rounded w-10" />
                    <div className="h-5 bg-slate-200 rounded w-20" />
                    <div className="h-5 bg-slate-200 rounded w-48 flex-1" />
                    <div className="h-5 bg-slate-200 rounded w-24" />
                    <div className="h-5 bg-slate-200 rounded w-24" />
                    <div className="h-5 bg-slate-200 rounded w-24" />
                  </div>
                ))}
              </div>
            </div>
          ) : rates.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <Globe className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No routes found</h3>
              <p className="text-slate-500">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full rate-table" data-testid="rates-table">
                  <thead>
                    <tr>
                      <th className="text-left">Country</th>
                      <th className="text-left">Prefix</th>
                      <th className="text-left">Destination</th>
                      <th className="text-left">Silver ({currencySymbol}/min)</th>
                      <th className="text-left">Gold ({currencySymbol}/min)</th>
                      <th className="text-left">Platinum ({currencySymbol}/min)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates.map((rate, index) => (
                      <motion.tr
                        key={`${rate.prefix}-${rate.route_name}-${index}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.02 }}
                        className="hover:bg-brand-25 transition-colors"
                      >
                        <td className="font-medium">
                          <span className="text-2xl mr-2">{getFlagEmoji(rate.iso)}</span>
                          <span className="text-slate-600 text-sm">{rate.country_name || rate.iso}</span>
                        </td>
                        <td className="font-mono text-slate-900">
                          {rate.prefix}
                          {rate.prefix_count > 1 && (
                            <span className="ml-1 text-xs text-slate-400">({rate.prefix_count})</span>
                          )}
                        </td>
                        <td className="font-medium text-slate-700">{rate.route_name}</td>
                        <td className="font-semibold text-slate-700">{formatPlanRate(rate.silver_rate, currencySymbol)}</td>
                        <td className="font-semibold text-brand-500">{formatPlanRate(rate.gold_rate, currencySymbol)}</td>
                        <td className="font-semibold text-slate-900">{formatPlanRate(rate.platinum_rate, currencySymbol)}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-8" data-testid="rates-pagination">
              <Button
                variant="outline"
                onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
                disabled={page === 1}
                className="rounded-full"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>

              <div className="flex items-center gap-2">
                {[...Array(Math.min(5, totalPages))].map((_, index) => {
                  let pageNum;

                  if (totalPages <= 5) {
                    pageNum = index + 1;
                  } else if (page <= 3) {
                    pageNum = index + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + index;
                  } else {
                    pageNum = page - 2 + index;
                  }

                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      onClick={() => setPage(pageNum)}
                      className={`w-10 h-10 rounded-full p-0 ${page === pageNum ? "bg-brand-500 hover:bg-brand-600" : ""}`}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
                disabled={page === totalPages}
                className="rounded-full"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </section>

      <section className="bg-slate-900 text-white py-12 noise-bg">
        <div className="container-custom text-center">
          <p className="text-slate-300 mb-4">
            Need custom rates or volume discounts? Contact our sales team.
          </p>
          <a href="https://t.me/voiphub_support" target="_blank" rel="noopener noreferrer">
            <Button className="bg-brand-500 hover:bg-brand-600 rounded-full px-6">
              Chat on Telegram
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default RatesPage;
